import { useEffect, useState } from 'react';
import { useAppConfig } from './useAppConfig.js';

export interface TorchCounterState {
  count: number;
  loading: boolean;
  /**
   * Optimistically increment the displayed count by 1. Use after the local
   * device's `incrementTorchesLit` Server.call resolves so the user sees
   * their own light immediately instead of waiting for the next 10s poll.
   *
   * The bump is auto-cleared when the backend count next updates (cleanup
   * avoids drift between the optimistic display and the canonical value).
   */
  bumpOptimistic: () => void;
  /**
   * Set the displayed count to a known value if it's higher than current.
   * Drives cross-client counter updates: when useTorchPresence receives a
   * `torch:lit` event from another bearer, the event carries the new total
   * count — `syncTo(event.count)` updates the local display without waiting
   * for the 60s useAppConfig revalidate window.
   */
  syncTo: (count: number) => void;
}

/**
 * Hook over useAppConfig that surfaces just the torches-lit count plus a
 * helper for optimistic updates. Backed by the module-level useAppConfig
 * cache; every consumer of useTorchCounter on the page sees the same value.
 */
export function useTorchCounter(): TorchCounterState {
  const { torchesLitCount: backendCount, loading } = useAppConfig();
  const [optimisticBumps, setOptimisticBumps] = useState(0);
  /** Highest count seen via live presence events; trumps backendCount if newer. */
  const [syncedCount, setSyncedCount] = useState(0);

  // Whenever the backend count moves, clear the optimistic offset. Either
  // the backend has caught up to (or surpassed) our optimistic bump, or
  // someone else's update has arrived — either way, the canonical value is
  // now authoritative and our local +N is no longer needed.
  useEffect(() => {
    setOptimisticBumps(0);
  }, [backendCount]);

  // Combine all sources: take the highest of backend, synced, and (backend +
  // optimistic). syncedCount lets cross-client live events update the display
  // without waiting for the 60s useAppConfig revalidate window.
  const displayedCount = Math.max(backendCount + optimisticBumps, syncedCount);

  return {
    count: displayedCount,
    loading,
    bumpOptimistic: () => setOptimisticBumps((n) => n + 1),
    syncTo: (count: number) => {
      // Authoritative value arriving from a backend response (own increment
      // or another bearer's LiveUpdate). Settles any optimistic offset so
      // we don't double-count (1 from our bump + 1 from the synced value).
      setSyncedCount((current) => (count > current ? count : current));
      setOptimisticBumps(0);
    },
  };
}
